import {
  useEquipmentCatalogue,
  useOrganisation,
  useOrganisationAccountingCategories,
  useOrganisationTaxRates,
  useOrganisationEquipmentTypes,
  useSetOrganisationUseProducts,
} from '@my/directory-api/hooks';
import {
  useCargoDefinitions,
  useCreateCargoDefinition,
  useDeleteCargoDefinition,
  useServiceTags,
  useUpdateCargoDefinition,
  PROPERTY_GROUP_KEYS,
  getCargoTypeLabel,
  getRatingUomLabel,
  getVariantPresets,
  groupHasValues,
  isGroupActive,
  parsePropertyGroups,
  serialisePropertyGroups,
  type CargoDefinition,
  type CargoTypeValue,
  type CreateCargoDefinitionRequest,
  type PropertyGroupKey,
  type RatingUomValue,
  type ServiceTag,
  type UpdateCargoDefinitionRequest,
} from '@my/tms-api';
import { ConfirmDialog, ErrorBanner, Loading, showToast } from '@my/ui';
import { ArrowRight, Edit, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrganisationTaxRate } from '@my/directory-api';
import { taxRateLabel, taxRateShort } from './lib/taxRateOptions';
import { Link } from 'react-router';
import { CargoDefinitionEditor } from './CargoDefinitionEditor';
import {
  buildProductsPayload,
  buildVariantsPayload,
  emptyDraft,
  orderedUomString,
  productsAreValid,
  toDraft,
  type CargoDefinitionDraft,
} from './lib/cargoDefinitionDraft';
import { PROPERTY_GROUP_LABELS } from './lib/cargoTypeConfig';

export interface CargoDefinitionsSectionProps {
  /** Owning organisation id; the section stays disabled while this is null. */
  organisationId: string | null;
}

/**
 * Settings → Cargo Definitions — an organisation's cargo definitions,
 * variants + property groups model. Its own page (it used to be a card on
 * Central Configuration), so it renders straight onto the page: a toolbar
 * row, then the grid — no card or second heading under the page title.
 *
 * <p>
 * The grid groups rows by Cargo Type and exposes columns: Cargo Type,
 * Display Name, Variants, Equipment, UoM, Properties, Tags, Actions.
 * Clicking the pencil reveals <see cref="CargoDefinitionEditor"/> below
 * the row. The +Add row pins to the top.
 * </p>
 *
 * Cross-feature reads:
 *  - Equipment chips in the editor are sourced from the org's
 *    `OrganisationEquipmentType` selection (Directory-side); operators
 *    can't bind a definition to equipment they haven't opted into.
 *  - Service tag chips are filtered to tags whose `applyToServiceTypes`
 *    includes `Cargo` (or is null/empty = applies-everywhere).
 */
export function CargoDefinitionsSection({ organisationId }: CargoDefinitionsSectionProps) {
  const { t } = useTranslation('configuration');
  const listQuery = useCargoDefinitions(organisationId);
  const createMutation = useCreateCargoDefinition();
  const updateMutation = useUpdateCargoDefinition();
  const deleteMutation = useDeleteCargoDefinition();

  // Cross-feature reads — both share the org id. The catalogue is small and
  // cached aggressively (24h staleTime) so the second open is instant.
  const catalogueQuery = useEquipmentCatalogue();
  const organisationQuery = useOrganisation(organisationId);
  const productsEnabled = organisationQuery.data?.useProducts ?? false;
  // Categories only when the organisation uses them; null hides the pickers.
  const categoriesEnabled = organisationQuery.data?.useAccountingCategories ?? false;
  const categoriesQuery = useOrganisationAccountingCategories(
    categoriesEnabled ? organisationId : null,
  );
  const accountingCategories = categoriesEnabled ? (categoriesQuery.data ?? []) : null;
  // The VAT-rate overrides choose from the organisation's Tax Schemas.
  const taxRatesQuery = useOrganisationTaxRates(organisationId);
  const taxRates = useMemo(() => taxRatesQuery.data ?? [], [taxRatesQuery.data]);
  const taxRatesById = useMemo(() => new Map(taxRates.map((r) => [r.id, r])), [taxRates]);
  const orgEquipmentQuery = useOrganisationEquipmentTypes(organisationId);
  const serviceTagsQuery = useServiceTags(organisationId);

  // `null` = no editor open; `'new'` = drafting; a guid = editing that row.
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<CargoDefinitionDraft>(emptyDraft());
  const [pendingDelete, setPendingDelete] = useState<CargoDefinition | null>(null);

  const isBusy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // ── Derived data ─────────────────────────────────────────────────────────

  const availableEquipment = useMemo(() => {
    const labelByKey = new Map<string, string>();
    for (const cls of catalogueQuery.data?.classes ?? []) {
      for (const group of cls.groups) {
        for (const type of group.types) labelByKey.set(type.key, type.label);
      }
    }
    return (orgEquipmentQuery.data ?? []).map((row) => ({
      key: row.catalogueKey,
      label: labelByKey.get(row.catalogueKey) ?? row.catalogueKey,
    }));
  }, [catalogueQuery.data, orgEquipmentQuery.data]);

  const cargoServiceTags = useMemo(
    () =>
      (serviceTagsQuery.data ?? []).filter(
        (tag) =>
          tag.status === 'Active' &&
          (!tag.applyToServiceTypes ||
            tag.applyToServiceTypes
              .split(',')
              .map((s) => s.trim())
              .includes('Cargo')),
      ),
    [serviceTagsQuery.data],
  );

  const tagsById = useMemo(() => {
    const map = new Map<string, ServiceTag>();
    for (const tag of serviceTagsQuery.data ?? []) map.set(tag.id, tag);
    return map;
  }, [serviceTagsQuery.data]);

  const equipmentLabelByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const eq of availableEquipment) map.set(eq.key, eq.label);
    return map;
  }, [availableEquipment]);

  const sortedDefs = useMemo(() => listQuery.data ?? [], [listQuery.data]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const startNew = () => {
    setEditingId('new');
    setDraft(emptyDraft());
  };

  const startEdit = (row: CargoDefinition) => {
    setEditingId(row.id);
    setDraft(toDraft(row));
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(emptyDraft());
  };

  const requestDelete = (row: CargoDefinition) => setPendingDelete(row);

  const handleSave = async () => {
    if (!organisationId || editingId === null) return;
    if (!draft.cargoType) return;

    const displayName = draft.displayName.trim();
    if (!displayName || draft.unitsOfMeasure.length === 0) return;

    const unitsOfMeasure = orderedUomString(draft.unitsOfMeasure);
    const propertyGroups = serialisePropertyGroups(draft.propertyGroups);
    const variants = buildVariantsPayload(draft, (type) => getVariantPresets(type));
    if (!productsAreValid(draft)) {
      showToast({ type: 'error', message: t('cargoDefinitions.products.invalid') });
      return;
    }
    const products = buildProductsPayload(draft);

    try {
      if (editingId === 'new') {
        const body: CreateCargoDefinitionRequest = {
          organisationId,
          cargoType: draft.cargoType,
          displayName,
          unitsOfMeasure,
          propertyGroups,
          variants,
          equipmentCatalogueKeys: [...draft.equipmentKeys],
          serviceTagIds: [...draft.serviceTagIds],
          ...products,
          accountingCategoryCode: draft.accountingCategoryCode || null,
          taxRateId: draft.taxRateId || null,
        };
        await createMutation.mutateAsync(body);
        showToast({ type: 'success', message: t('cargoDefinitions.added') });
      } else {
        const body: UpdateCargoDefinitionRequest = {
          cargoDefinitionId: editingId,
          cargoType: draft.cargoType,
          // Echoed from the row being edited. This editor has no status control,
          // but the update is a full replace whose command DEFAULTS to Active —
          // so omitting it would quietly bring a retired definition back.
          status: sortedDefs.find((d) => d.id === editingId)?.status ?? 'Active',
          displayName,
          unitsOfMeasure,
          propertyGroups,
          variants,
          equipmentCatalogueKeys: [...draft.equipmentKeys],
          serviceTagIds: [...draft.serviceTagIds],
          ...products,
          accountingCategoryCode: draft.accountingCategoryCode || null,
          taxRateId: draft.taxRateId || null,
        };
        await updateMutation.mutateAsync({ organisationId, body });
        showToast({ type: 'success', message: t('cargoDefinitions.updated') });
      }
      cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('cargoDefinitions.saveFailed'),
      });
    }
  };

  const confirmDeletePending = async () => {
    const row = pendingDelete;
    if (!row || !organisationId) return;
    try {
      await deleteMutation.mutateAsync({ organisationId, id: row.id });
      showToast({ type: 'success', message: t('cargoDefinitions.removed') });
      if (editingId === row.id) cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('cargoDefinitions.deleteFailed'),
      });
    } finally {
      setPendingDelete(null);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const headerAction =
    listQuery.isSuccess && editingId === null ? (
      <button
        type="button"
        aria-label={t('cargoDefinitions.addAria')}
        onClick={startNew}
        disabled={!organisationId || isBusy}
        className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
      >
        <Plus className="h-3.5 w-3.5" />
        {t('cargoDefinitions.add')}
      </button>
    ) : null;

  return (
    <>
      {/* Toolbar: the products switch on the left (products are the next level
          down from cargo, and the Products page sits right after this one),
          +Add on the right where Resources and Products keep it. */}
      <div className="flex flex-wrap items-center gap-3">
        <UseProductsSwitch organisationId={organisationId} />
        <div className="flex-1" />
        {headerAction}
      </div>

      {listQuery.isPending && <Loading label={t('cargoDefinitions.loading')} />}
      {listQuery.isError && (
        <ErrorBanner
          title={t('cargoDefinitions.loadFailed')}
          message={listQuery.error.message}
        />
      )}

      {listQuery.isSuccess && (
        <div className="overflow-x-auto">
          <div className="min-w-[860px] overflow-hidden rounded-md">
            <table className="w-full table-auto text-xs">
              <thead className="bg-slate-700 text-[11px] uppercase tracking-wide text-white">
                <tr>
                  <Th>{t('cargoDefinitions.table.cargoType')}</Th>
                  <Th>{t('cargoDefinitions.table.displayName')}</Th>
                  <Th>{t('cargoDefinitions.table.variants')}</Th>
                  <Th>{t('cargoDefinitions.table.equipment')}</Th>
                  <Th>{t('cargoDefinitions.table.uom')}</Th>
                  <Th>{t('cargoDefinitions.table.properties')}</Th>
                  <Th>{t('cargoDefinitions.table.tags')}</Th>
                  <Th align="right">{t('cargoDefinitions.table.actions')}</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-500 bg-white">
                {editingId === 'new' && (
                  <tr>
                    <td colSpan={8} className="bg-slate-50 px-3 py-3">
                      <CargoDefinitionEditor
                        draft={draft}
                        isNew
                        onChange={setDraft}
                        onSave={handleSave}
                        onCancel={cancel}
                        isBusy={isBusy}
                        availableEquipment={availableEquipment}
                        availableServiceTags={cargoServiceTags}
                        organisationId={organisationId}
                        productsEnabled={productsEnabled}
                        accountingCategories={accountingCategories}
                        taxRates={taxRates}
                      />
                    </td>
                  </tr>
                )}

                {sortedDefs.map((def, idx) => {
                  const isEditing = editingId === def.id;
                  const prev = idx > 0 ? sortedDefs[idx - 1] : undefined;
                  const isFirstInGroup = !prev || prev.cargoType !== def.cargoType;
                  const isGroupBoundary = idx > 0 && isFirstInGroup;

                  return (
                    <DefinitionRow
                      key={def.id}
                      def={def}
                      isEditing={isEditing}
                      isFirstInGroup={isFirstInGroup}
                      isGroupBoundary={isGroupBoundary}
                      rowsLocked={editingId !== null && !isEditing}
                      onClick={() => {
                        if (editingId === null && !isBusy) startEdit(def);
                      }}
                      onEditClick={() => startEdit(def)}
                      equipmentLabelByKey={equipmentLabelByKey}
                      tagsById={tagsById}
                      taxRatesById={taxRatesById}
                    >
                      {isEditing && (
                        <tr>
                          <td colSpan={8} className="bg-slate-50 px-3 py-3">
                            <CargoDefinitionEditor
                              draft={draft}
                              isNew={false}
                              onChange={setDraft}
                              onSave={handleSave}
                              onCancel={cancel}
                              onDelete={() => requestDelete(def)}
                              isBusy={isBusy}
                              availableEquipment={availableEquipment}
                              availableServiceTags={cargoServiceTags}
                        organisationId={organisationId}
                        productsEnabled={productsEnabled}
                        accountingCategories={accountingCategories}
                        taxRates={taxRates}
                            />
                          </td>
                        </tr>
                      )}
                    </DefinitionRow>
                  );
                })}

                {sortedDefs.length === 0 && editingId !== 'new' && (
                  <tr>
                    <td colSpan={8} className="px-3 py-4 text-center text-xs italic text-slate-400">
                      {t('cargoDefinitions.empty', {
                        add: t('cargoDefinitions.addDefinition'),
                      })}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('cargoDefinitions.deleteDialog.title')}
        message={
          pendingDelete
            ? t('cargoDefinitions.deleteDialog.message', { name: pendingDelete.displayName })
            : null
        }
        confirmLabel={t('cargoDefinitions.deleteDialog.confirm')}
        onConfirm={confirmDeletePending}
        onCancel={() => setPendingDelete(null)}
        busy={deleteMutation.isPending}
      />
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Products switch
// ────────────────────────────────────────────────────────────────────────────

/**
 * "Use Products" — whether this organisation records which product each order
 * carries. Lives in the Cargo Definitions header because products are the
 * next level down from cargo: a cargo definition says HOW it travels (iso
 * tank, pallet), a product says WHAT is in it.
 *
 * <p>
 * Saves on toggle, through its own endpoint (the organisation PUT would need
 * the whole organisation echoed back). On, it adds Settings → Products to the
 * menu; off, it hides it and the order field but keeps the list, so switching
 * back finds everything where it was.
 * </p>
 */
function UseProductsSwitch({ organisationId }: { organisationId: string | null }) {
  const { t } = useTranslation(['configuration', 'common']);
  const organisationQuery = useOrganisation(organisationId);
  const saveMutation = useSetOrganisationUseProducts();
  const enabled = organisationQuery.data?.useProducts ?? false;

  const onToggle = async (next: boolean) => {
    if (!organisationId) return;
    try {
      await saveMutation.mutateAsync({ organisationId, useProducts: next });
      showToast({
        type: 'success',
        message: next ? t('cargoDefinitions.useProductsOn') : t('cargoDefinitions.useProductsOff'),
      });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('cargoDefinitions.useProductsFailed'),
      });
    }
  };

  return (
    // Same inline label + bare toggle as the Plan cockpit's GANTT switch — a
    // toolbar control, not a form field.
    <span className="flex items-center gap-3">
      <span className="flex items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          {t('cargoDefinitions.useProducts')}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label={t('cargoDefinitions.useProducts')}
          disabled={!organisationQuery.isSuccess || saveMutation.isPending}
          onClick={() => void onToggle(!enabled)}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ease-in-out disabled:cursor-not-allowed disabled:opacity-60 ${
            enabled ? 'bg-blue-600' : 'bg-slate-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out ${
              enabled ? 'translate-x-4' : 'translate-x-0.5'
            }`}
          />
        </button>
      </span>
      {enabled && (
        <Link
          to="/settings/products"
          className="flex items-center gap-1 text-xs font-medium text-purple-600 hover:underline"
        >
          {t('cargoDefinitions.manageProducts')}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      )}
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Read-only display row + helpers
// ────────────────────────────────────────────────────────────────────────────

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th
      className={`whitespace-nowrap px-3 py-2 font-semibold ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}

interface DefinitionRowProps {
  def: CargoDefinition;
  isEditing: boolean;
  isFirstInGroup: boolean;
  isGroupBoundary: boolean;
  rowsLocked: boolean;
  onClick: () => void;
  onEditClick: () => void;
  equipmentLabelByKey: Map<string, string>;
  tagsById: Map<string, ServiceTag>;
  children?: React.ReactNode;
  taxRatesById: Map<string, OrganisationTaxRate>;
}

function DefinitionRow({
  def,
  isEditing,
  isFirstInGroup,
  isGroupBoundary,
  rowsLocked,
  onClick,
  onEditClick,
  equipmentLabelByKey,
  tagsById,
  taxRatesById,
  children,
}: DefinitionRowProps) {
  const { t } = useTranslation('configuration');
  const rowClass = [
    isEditing ? 'bg-slate-50' : rowsLocked ? 'opacity-50' : 'cursor-pointer hover:bg-slate-50',
    isGroupBoundary ? '!border-t-2 !border-t-slate-600' : '',
  ].join(' ');

  const equipmentLabels = def.equipmentCatalogueKeys.map((k) => equipmentLabelByKey.get(k) ?? k);
  const propertyGroups = useMemo(() => parsePropertyGroups(def.propertyGroupsJson), [def.propertyGroupsJson]);

  return (
    <>
      <tr className={rowClass} onDoubleClick={rowsLocked || isEditing ? undefined : onClick}>
        <td className="whitespace-nowrap px-3 py-0.5 align-top font-semibold text-slate-900">
          {isFirstInGroup ? getCargoTypeLabel(def.cargoType) : ''}
        </td>
        <td className="whitespace-nowrap px-3 py-0.5 align-top text-slate-700">
          {def.displayName}
          {def.accountingCategoryCode && (
            <span
              className="ml-1.5 rounded-sm bg-violet-50 px-1 py-0.5 font-mono text-[10px] font-semibold text-violet-700"
              title={t('cargoDefinitions.accountingCategory.badgeTitle')}
            >
              {def.accountingCategoryCode}
            </span>
          )}
          {def.taxRateId && (
            <span
              className="ml-1.5 rounded-sm bg-sky-50 px-1 py-0.5 text-[10px] font-semibold text-sky-700"
              title={t('cargoDefinitions.vat.badgeTitle', {
                rate: taxRatesById.get(def.taxRateId)
                  ? taxRateLabel(taxRatesById.get(def.taxRateId)!)
                  : t('cargoDefinitions.vat.notInList'),
              })}
            >
              {taxRatesById.get(def.taxRateId) ? taxRateShort(taxRatesById.get(def.taxRateId)!) : '?'} VAT
            </span>
          )}
        </td>
        <td className="px-3 py-0.5 align-top text-slate-500">
          <VariantsSummary def={def} />
        </td>
        <td className="px-3 py-0.5 align-top text-slate-500">
          {equipmentLabels.length > 0 ? (
            equipmentLabels.join(', ')
          ) : (
            <span className="italic text-slate-300">—</span>
          )}
        </td>
        <td className="px-3 py-0.5 align-top text-slate-500">
          <UomSummary raw={def.unitsOfMeasure} />
        </td>
        <td className="px-3 py-0.5 align-top">
          <PropertyChips payload={propertyGroups} def={def} />
        </td>
        <td className="px-3 py-0.5 align-top">
          <div className="flex flex-wrap gap-1">
            {def.serviceTagIds.length === 0 ? (
              <span className="italic text-slate-300">—</span>
            ) : (
              def.serviceTagIds.map((id) => {
                const tag = tagsById.get(id);
                return (
                  <span
                    key={id}
                    className="inline-flex items-center whitespace-nowrap rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700"
                  >
                    {tag?.name ?? id}
                  </span>
                );
              })
            )}
          </div>
        </td>
        <td className="whitespace-nowrap px-3 py-0.5 align-top text-right">
          <button
            type="button"
            aria-label={t('cargoDefinitions.ariaEdit', { name: def.displayName })}
            onClick={(e) => {
              e.stopPropagation();
              if (!rowsLocked) onEditClick();
            }}
            disabled={rowsLocked}
            className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
          >
            <Edit className="h-3.5 w-3.5" />
          </button>
        </td>
      </tr>
      {children}
    </>
  );
}

function VariantsSummary({ def }: { def: CargoDefinition }) {
  // Aggregator types deliberately have no variants — at order entry items
  // come from other definitions. Surface the visual cue rather than "—".
  if (def.cargoType === 'FullLoad' || def.cargoType === 'PartLoad') {
    return <span className="italic text-slate-300">(items at order)</span>;
  }
  const names = def.variants.map((v) => v.name);
  if (names.length === 0) return <span className="italic text-slate-300">—</span>;
  if (names.length <= 3) return <>{names.join(', ')}</>;
  return <>{names.slice(0, 3).join(', ')} +{names.length - 3}</>;
}

function UomSummary({ raw }: { raw: string }) {
  if (!raw) return <span className="italic text-slate-300">—</span>;
  const parts = raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean) as RatingUomValue[];
  return <>{parts.map((p) => getRatingUomLabel(p)).join(', ')}</>;
}

/**
 * Tiny chip per ticked property group — shows the group label + a tiny
 * `spec`/`prompt` indicator. Same vocabulary as the mock + editor.
 */
function PropertyChips({
  payload,
  def,
}: {
  payload: ReturnType<typeof parsePropertyGroups>;
  def: CargoDefinition;
}) {
  const { t } = useTranslation('configuration');
  const active = PROPERTY_GROUP_KEYS.filter((k) => isGroupActive(k, payload));
  if (active.length === 0 && !def.hasProducts) return <span className="italic text-slate-300">—</span>;
  const linked = def.products ?? [];
  return (
    <span className="flex flex-wrap gap-1">
      {active.map((group) => (
        <PropertyChipBadge key={group} group={group} payload={payload} />
      ))}
      {def.hasProducts && (
        <span
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-purple-50 px-1.5 py-0.5 text-[10px] font-medium text-purple-800"
          title={linked.map((p) => p.productName).join(', ') || undefined}
        >
          {linked.length === 0
            ? t('cargoDefinitions.products.badgeAny')
            : t('cargoDefinitions.products.badgeCount', { count: linked.length })}
        </span>
      )}
    </span>
  );
}

function PropertyChipBadge({
  group,
  payload,
}: {
  group: PropertyGroupKey;
  payload: ReturnType<typeof parsePropertyGroups>;
}) {
  const { t } = useTranslation('configuration');
  const filled = groupHasValues(group, payload);
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
        filled
          ? 'bg-slate-100 text-slate-700'
          : 'border border-dashed border-slate-300 bg-white text-slate-500'
      }`}
      title={
        filled
          ? t('cargoDefinitions.propertyChipSpec')
          : t('cargoDefinitions.propertyChipPrompt')
      }
    >
      {PROPERTY_GROUP_LABELS[group]}
      <span className="text-[8px] font-semibold uppercase opacity-60">
        {filled
          ? t('cargoDefinitions.propertyChipBadgeSpec')
          : t('cargoDefinitions.propertyChipBadgePrompt')}
      </span>
    </span>
  );
}

// Keep type re-export for callers that may have imported it through this module.
export type { CargoTypeValue };
